import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Nav, Spinner } from 'react-bootstrap';
import { RotateCw } from 'lucide-react';
import { apiErrorMessage, marketingSiteService } from '../../services/apiService';
import { useAuthToken } from '../../hooks/useAuthToken';
import { ErrorState } from '../../components/PageState';
import { SeverityIcon } from './SeoFindings';
import { SEVERITY_ORDER, severitySummary } from './seoRules';
import { shortSha, timeAgo } from './ui';
import LinksReport from './LinksReport';
import IndexReport from './IndexReport';
import FaqReport from './FaqReport';

const SEVERITIES = [
  { key: 'ERROR', label: 'Errors' },
  { key: 'WARN', label: 'Warnings' },
  { key: 'INFO', label: 'Notes' },
];

/** Site-wide SEO report: every page's findings, filterable by severity and rule. */
const SeoReport = ({ onOpenPage }) => {
  const ensureToken = useAuthToken();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [severities, setSeverities] = useState(() => new Set(['ERROR', 'WARN']));
  const [rule, setRule] = useState(null);
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await ensureToken();
      setReport((await marketingSiteService.siteLint()).data);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not run the SEO check.'));
    } finally {
      setLoading(false);
    }
  }, [ensureToken]);

  useEffect(() => {
    load();
  }, [load]);

  const rules = useMemo(
    () =>
      [...(report?.rules || [])].sort(
        (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity] || b.findings - a.findings
      ),
    [report]
  );

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (report?.findings || [])
      .filter((f) => (rule ? f.rule === rule : severities.has(f.severity)))
      .filter((f) => !q || f.url.toLowerCase().includes(q) || f.message.toLowerCase().includes(q))
      .sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity] || a.url.localeCompare(b.url));
  }, [report, severities, rule, query]);

  const toggleSeverity = (key) => {
    setRule(null);
    setSeverities((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (loading && !report) {
    return (
      <div className="mse-empty">
        <Spinner animation="border" size="sm" /> Checking every page
      </div>
    );
  }
  if (error) {
    return <ErrorState message={error} onRetry={load} />;
  }
  if (!report) return null;

  return (
    <div className="mse-health-report">
      <div className="mse-health-summary">
        <strong>{severitySummary(report.counts)}</strong>
        <span className="mse-hint">
          across {report.pages} pages on the test site at <code>{shortSha(report.headSha)}</code>, checked{' '}
          {timeAgo(report.computedAt)}
        </span>
        <Button
          size="sm"
          variant="outline-secondary"
          className="ms-auto"
          onClick={load}
          disabled={loading}
          title="Check again"
          aria-label="Check again"
        >
          {loading ? <Spinner animation="border" size="sm" /> : <RotateCw size={14} />}
        </Button>
      </div>

      <div className="mse-health-filters">
        {SEVERITIES.map(({ key, label }) => (
          <Form.Check
            key={key}
            inline
            type="checkbox"
            id={`sev-${key}`}
            label={
              <>
                <SeverityIcon severity={key} /> {label} ({report.counts[key] || 0})
              </>
            }
            checked={!rule && severities.has(key)}
            onChange={() => toggleSeverity(key)}
          />
        ))}
        <Form.Control
          size="sm"
          type="search"
          placeholder="Filter by page or text"
          aria-label="Filter findings by page or text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="mse-health-search"
        />
      </div>

      <div className="mse-rule-chips">
        {rules.map((r) => (
          <button
            key={r.rule}
            type="button"
            className={`mse-rule-chip${rule === r.rule ? ' active' : ''}`}
            onClick={() => setRule(rule === r.rule ? null : r.rule)}
            title={`${r.findings} finding${r.findings === 1 ? '' : 's'} on ${r.pages} page${r.pages === 1 ? '' : 's'}`}
          >
            <SeverityIcon severity={r.severity} size={12} /> {r.title} <span className="mse-group-count">{r.pages}</span>
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="mse-empty">Nothing to show for this filter.</div>
      ) : (
        <div className="mse-health-table-wrap">
          <table className="mse-health-table mse-findings-table">
            <thead>
              <tr>
                <th aria-label="Severity" />
                <th>Page</th>
                <th>Issue</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((f, i) => (
                <tr key={`${f.path}-${f.rule}-${i}`}>
                  <td>
                    <SeverityIcon severity={f.severity} />
                  </td>
                  <td>
                    <button type="button" className="mse-link-button mse-mono" onClick={() => onOpenPage(f.path)}>
                      {f.url}
                    </button>
                  </td>
                  <td>
                    <div className="mse-finding-title">{f.title}</div>
                    <div className="mse-finding-message">{f.message}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const TABS = [
  { key: 'seo', label: 'SEO' },
  { key: 'links', label: 'Links' },
  { key: 'index', label: 'Sitemap and llms.txt' },
  { key: 'faq', label: 'FAQs' },
];

/**
 * Cross-page reports, one tab each. Saves made here call onSaved (the release bar refreshes at once),
 * and onDraftChange reports an unsaved llms.txt draft so leaving can ask first.
 */
const SiteHealth = ({ tab, onTab, canEdit, siteUrl, onOpenPage, onOpenShared, onIndexStatus, onSaved, onDraftChange }) => {
  const tabs = TABS;
  const active = tabs.some((t) => t.key === tab) ? tab : 'seo';
  return (
    <section className="mse-health">
      <div className="mse-toolbar">
        <strong className="mse-health-title">Site health</strong>
        <Nav variant="pills" activeKey={active} onSelect={(k) => onTab(k)} className="mse-health-tabs">
          {tabs.map((t) => (
            <Nav.Item key={t.key}>
              <Nav.Link eventKey={t.key}>{t.label}</Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
      </div>
      <div className="mse-health-body">
        {active === 'seo' && <SeoReport onOpenPage={onOpenPage} />}
        {active === 'links' && (
          <LinksReport canEdit={canEdit} siteUrl={siteUrl} onOpenPage={onOpenPage} onOpenShared={onOpenShared} onSaved={onSaved} />
        )}
        {active === 'index' && (
          <IndexReport
            canEdit={canEdit}
            siteUrl={siteUrl}
            onOpenPage={onOpenPage}
            onStatus={onIndexStatus}
            onSaved={onSaved}
            onDraftChange={onDraftChange}
          />
        )}
        {active === 'faq' && <FaqReport canEdit={canEdit} siteUrl={siteUrl} onOpenPage={onOpenPage} onSaved={onSaved} />}
      </div>
    </section>
  );
};

export default SiteHealth;
